import { defineStore } from 'pinia';
import { resolveAuthErrorMessage, type AuthSubmitMode } from '@/entities/session/auth-errors';
import {
  loginPlayer,
  loginPlayerAsGuest,
  loginPlayerByGoogle,
  loginPlayerBySms,
  registerPlayer,
} from '@/features/auth/auth-api';
import { getProfile, updateProfile, type ProfileUpdateRequest } from '@/features/profile/profile-api';
import { resolveRestoredSession, type SessionRestoreStatus } from '@/entities/session/session-expiry';
import { tokenStorage } from '@/entities/session/token-storage';
import type {
  LoginRequest,
  PlayerAuthResponse,
  PlayerSummary,
  RegisterRequest,
} from '@/entities/session/types';
import { clientEnv } from '@/shared/config/env';
import { reportEvent, type MonitoringEvent } from '@/shared/monitoring/monitoring';
import { activeMetaPixelId, initMetaPixel, trackMeta } from '@/shared/tracking/meta-pixel';
import { storedAgentCode, storedReferralCode } from '@/shared/referral/invite-link';
import { rememberSimulationPending } from '@/shared/simulation/simulation-session';
import { clearSimulationSession, restoreSimulationSession } from '@/shared/simulation/simulation-state';

/** 打点失败不能把已经成功的登录/注册冲成失败。 */
function reportSessionEvent(event: MonitoringEvent) {
  try {
    reportEvent(event);
  } catch {
    // ignore
  }
}

function pageHost() {
  return typeof window === 'undefined' ? undefined : window.location.hostname;
}

interface SessionState {
  expiresAt: string;
  lastError: string;
  player: PlayerSummary | null;
  submitting: boolean;
  token: string;
}

const applyAuthResponse = (state: SessionState, response: PlayerAuthResponse) => {
  state.expiresAt = response.expiresAt;
  state.player = response.player;
  state.token = response.accessToken;
  tokenStorage.write(response.accessToken, response.expiresAt);
  rememberSimulationPending(response.player.accountKind, response.player.simulationApiBaseUrl);
};

export const useSessionStore = defineStore('session', {
  actions: {
    clearSession() {
      this.expiresAt = '';
      this.lastError = '';
      this.player = null;
      this.token = '';
      tokenStorage.clear();
      clearSimulationSession();
    },
    clearError() {
      this.lastError = '';
    },
    restoreSession(): SessionRestoreStatus {
      const restored = resolveRestoredSession(tokenStorage.read(), tokenStorage.readExpiresAt());
      if (restored.status === 'expired') {
        tokenStorage.clear();
        this.expiresAt = '';
        this.token = '';
        clearSimulationSession();
        return 'expired';
      }
      this.expiresAt = restored.expiresAt;
      this.token = restored.token;
      if (restored.status === 'active') restoreSimulationSession();
      else clearSimulationSession();
      return restored.status;
    },
    patchPlayer(player: PlayerSummary) {
      this.player = player;
    },
    async fetchProfile() {
      if (!this.token) return false;
      try {
        this.player = await getProfile(this.token);
        rememberSimulationPending(this.player.accountKind, this.player.simulationApiBaseUrl);
        return true;
      } catch (error) {
        reportSessionEvent({
          context: { error: error instanceof Error ? error.message : String(error) },
          event: 'profile.fetch.failed',
          level: 'warn',
        });
        return false;
      }
    },
    async saveProfile(payload: Omit<ProfileUpdateRequest, 'siteId'>) {
      if (!this.token) throw new Error('NO_SESSION');
      this.player = await updateProfile(this.token, payload);
      rememberSimulationPending(this.player.accountKind, this.player.simulationApiBaseUrl);
      return this.player;
    },
    async login(payload: Omit<LoginRequest, 'siteId'>) {
      return this.submitAuth('login', () =>
        loginPlayer({
          ...payload,
          siteId: clientEnv.siteId,
        }),
      );
    },
    async register(payload: Omit<RegisterRequest, 'siteId'>) {
      return this.submitAuth('register', () =>
        registerPlayer({
          ...payload,
          agentCode: payload.agentCode || storedAgentCode() || undefined,
          registerHost: pageHost(),
          siteId: clientEnv.siteId,
        }),
      );
    },
    async loginBySms(payload: { phone: string; code: string; referralCode?: string }) {
      return this.submitAuth('login', () =>
        loginPlayerBySms({
          ...payload,
          agentCode: storedAgentCode() || undefined,
          registerHost: pageHost(),
          siteId: clientEnv.siteId,
        }),
      );
    },
    /** 游客登录：服务端创建 guest 玩家并签发真实会话。 */
    async loginAsGuest() {
      return this.submitAuth('login', () =>
        loginPlayerAsGuest({
          agentCode: storedAgentCode() || undefined,
          referralCode: storedReferralCode() || undefined,
          registerHost: pageHost(),
          siteId: clientEnv.siteId,
        }),
      );
    },
    async loginByGoogle(payload: { agentCode?: string; idToken: string; referralCode?: string }) {
      return this.submitAuth('login', () =>
        loginPlayerByGoogle({
          ...payload,
          agentCode: payload.agentCode || storedAgentCode() || undefined,
          registerHost: pageHost(),
          siteId: clientEnv.siteId,
        }),
      );
    },
    async submitAuth(mode: AuthSubmitMode, requester: () => Promise<PlayerAuthResponse>) {
      if (this.submitting) {
        return false;
      }
      this.submitting = true;
      this.lastError = '';

      try {
        const response = await requester();
        applyAuthResponse(this, response);
        const playerId = response.player.playerUserId;
        const boundPixel = response.player.metaPixelId || '';
        if (boundPixel) initMetaPixel(boundPixel);
        if (boundPixel && activeMetaPixelId() === boundPixel) {
          if (response.newPlayer) {
            trackMeta('CompleteRegistration', { eventId: `reg_${playerId}` });
          } else {
            trackMeta('login', { eventId: `login_${playerId}_${Math.floor(Date.now() / 1000)}` });
          }
        }
        reportSessionEvent({
          context: {
            playerUserId: response.player.playerUserId,
            siteId: response.player.siteId,
          },
          event: 'auth.session.started',
          level: 'info',
        });
        return true;
      } catch (error) {
        this.lastError = resolveAuthErrorMessage(error, mode);
        reportSessionEvent({
          context: {
            error: error instanceof Error ? error.message : String(error),
            mode,
          },
          event: 'auth.submit.failed',
          level: 'warn',
        });
        return false;
      } finally {
        this.submitting = false;
      }
    },
  },
  getters: {
    isAuthenticated: (state) => Boolean(state.token),
    displayName: (state) => state.player?.displayName || state.player?.loginName || 'Visitante',
  },
  state: (): SessionState => ({
    expiresAt: '',
    lastError: '',
    player: null,
    submitting: false,
    token: '',
  }),
});
